import { Flex, Grid, NumberInput, Group } from '@mantine/core'
import React, {
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNotEmpty, useForm } from '@mantine/form'
import Select from '../inputs/Select'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { EntityParams } from 'app/models/entity-params'
import { getLoanLendersPositionsWithLoading } from 'app/state/ducks/lenders-positions/selectors'
import {
    changeAllLendersForLoan,
    loadLendersPositionsByLoan,
} from 'app/state/ducks/lenders-positions/thunks'
import { StableKey } from 'app/models/common-types'
import {
    ErrorNotification,
    SuccessNotification,
} from '../notifications/notification'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { LoanParams } from 'app/models/loan-params'
import {
    formatNumberToCurrency,
} from 'app/utils/util-functions'
import IonIcon from '@reacticons/ionicons'
import { EntityRole } from 'app/models/entity-profile-params'
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import {
    getLoanAmendmentsByFacility,
} from 'app/state/ducks/loan-amendments/selectors'
import { LendersPosition } from 'app/models/lenders-position-params'
import { saveLoanAmendment } from 'app/state/ducks/loan-amendments/thunks'

type Props = {
    loan: LoanParams
    setLoan: any
    amendment?: boolean
    asOfDate?: string
}

interface ILendersArray {
    label: string
    value: string
    disabled: boolean
}

export interface ChildManageLendersRef {
    handleClick: () => void
}

const defaultValue = {
    id: null,
    percentage: '',
    amount: 0,
    lenderName: '',
    lenders: { id: '' },
    creditContactId: null,
    adminContactId: null,
    wireInstructions: [],
    newField: true as boolean | null,
}

const ManageLoanLenders: React.ForwardRefRenderFunction<
    ChildManageLendersRef,
    Props
> = ({ loan, amendment, asOfDate, setLoan }, ref) => {
    const dispatch = useDispatch()
    const facility = useSelector(getFacility)
    const entities: EntityParams[] = useSelector(getEntities)
    const entityProfiles = useSelector(getEntityProfiles)
    const loanAmenedmentFacility = useSelector(getLoanAmendmentsByFacility)
    const { data: lendersPositionsData, isLoading: isLoadingLenders } =
        useSelector(getLoanLendersPositionsWithLoading)

    // ------- Load lender positions once per loan.id (guard StrictMode double-run) --------
    const fetchedForLoanIdRef = useRef<string | null>(null)
    const previousStatusRef = useRef<string | null>(null)
    const previousAmendmentRef = useRef<boolean>(false)

    useEffect(() => {
        if (!loan?.id) return

        // Reload if:
        // 1. Loan ID changed
        // 2. Status changed (e.g., Draft -> Approved)
        // 3. Amendment mode changed (opening edit/amendment on approved loan)
        const shouldReload =
            fetchedForLoanIdRef.current !== loan.id ||
            previousStatusRef.current !== loan.status ||
            previousAmendmentRef.current !== amendment

        if (!shouldReload) return

        fetchedForLoanIdRef.current = loan.id
        previousStatusRef.current = loan.status || null
        previousAmendmentRef.current = amendment || false
        dispatch(loadLendersPositionsByLoan(loan.id))
    }, [loan?.id, loan?.status, amendment, dispatch])

    // ------- Source of truth for positions: amendment list (if present) else store data ------
    const basePositions: LendersPosition[] = useMemo(() => {
        // If this is a draft amendment with amendmentLenders, use those
        if (loan?.status === 'Draft' && loan?.amendmentType && loan?.amendmentLenders && loan.amendmentLenders.length > 0) {
            return loan.amendmentLenders as LendersPosition[]
        }
        // Otherwise, use the ledger positions (current approved positions)
        return lendersPositionsData ?? []
    }, [loan?.status, loan?.amendmentType, loan?.amendmentLenders, lendersPositionsData])    // ------- Compute effective amounts - always use the latest proRata --------
    // Positions must reflect the pro-rata in force ON the action's effective
    // date, not today's latest — same as-of resolution as loanAmountsEntryAsOf
    // (effectiveDate <= date, processedTime tie-break, YYYY-MM-DD string
    // comparison). The caller passes asOfDate explicitly (amendment date for
    // amendments, loan start date for edits); the amendment-date fallback keeps
    // older call sites working. Falls back to the latest pro-rata when there's
    // no as-of date or no entry on/before it.
    const asOfDateStr =
        asOfDate ?? (amendment && loan?.amendmentDate ? loan.amendmentDate : null)
    const effectiveLendersPositions: LendersPosition[] = useMemo(() => {
        return (basePositions ?? []).map((lp) => {
            if (!lp?.proRatas || lp.proRatas.length === 0) return lp

            const eligible = asOfDateStr
                ? lp.proRatas.filter((pr) => pr.effectiveDate <= asOfDateStr)
                : lp.proRatas
            const pool = eligible.length > 0 ? eligible : lp.proRatas
            const sorted = [...pool].sort(
                (a, b) =>
                    a.effectiveDate.localeCompare(b.effectiveDate) ||
                    (a.processedTime ?? '').localeCompare(b.processedTime ?? ''),
            )
            const effectiveProRata = sorted[sorted.length - 1]
            const effectiveAmount = effectiveProRata ? effectiveProRata.amount : '0'

            return { ...lp, amount: effectiveAmount }
        })
    }, [basePositions, asOfDateStr])


    const disableLenderPositions = false
    // const disableLenderPositions = useMemo(() => {
    //     if (loan?.isNonProRata || loan?.isSwingLine) return true
    //     return hasLatestProRataAfterStart
    // }, [loan?.isNonProRata, loan?.isSwingLine, hasLatestProRataAfterStart])

    // ------- Lenders dropdown options ------------------------------------------------
    const [lendersArray, setLendersArray] = useState<ILendersArray[]>([])

    useEffect(() => {
        if (loan?.isSwingLine) {
            const lenderallocations =
                facility?.subLimits
                    .find((subLimit) => subLimit.subLimitsType === 'SwingLine')
                    ?.lenders.map((lenders: any) => {
                        const entity = entities.find((entity) => entity.id === lenders.lender.id)
                        return {
                            value: lenders.lender.id ?? '',
                            label: entity?.entityName ?? '',
                            disabled: false,
                        }
                    }) ?? []
            setLendersArray(lenderallocations)
            return
        }

        const lenderallocations =
            entities && entities.length > 0
                ? entities
                    ?.filter((entity) =>
                        entity.entityProfile.some((profile) =>
                            entityProfiles
                                .find((option) => option.id === profile.id)
                                ?.roles.includes(EntityRole.LenderRole)
                        )
                    )
                    .map((entity) => ({
                        value: entity.id || '',
                        label: entity.entityName,
                        disabled: false,
                    }))
                : []
        setLendersArray(lenderallocations)
    }, [loan, facility, entities, entityProfiles])

    // ------- Form -------------------------------------------------------------------
    const form = useForm({
        initialValues: {
            lendersPosition:
                (effectiveLendersPositions?.length ?? 0) > 0
                    ? effectiveLendersPositions.map(
                        (lp: { lender: StableKey; amount: string; id?: string }) => ({
                            lenders: lp.lender,
                            amount: Number(lp.amount),
                            id: lp.id,
                        })
                    )
                    : [{ ...defaultValue }],
            subtotal:
                (effectiveLendersPositions?.length ?? 0) > 0
                    ? effectiveLendersPositions.reduce(
                        (accumulator: number, lp: { amount: string }) =>
                            accumulator + Number(lp.amount),
                        0
                    )
                    : 0,
        },

        transformValues: (values) => ({
            ...values,
            lendersPosition: values.lendersPosition.map(
                (a: { lenders: { id: string }; amount: number }) => {
                    return {
                        ...a,
                        lender: {
                            id: a.lenders.id,
                            admin:
                                entities.find((entity) => entity.id === a.lenders.id)?.maker ??
                                '',
                        },
                        amount: a.amount.toString(),
                    }
                }
            ),
        }),

        validate: {
            lendersPosition: {
                amount: (value) => (value >= 0 ? null : 'Invalid amount'),
                lenders: { id: isNotEmpty(' Choose a lender') },
            },
        },
    })

    // Reseed form when effective positions change
    useEffect(() => {
        const lendersPosition =
            (effectiveLendersPositions?.length ?? 0) > 0
                ? effectiveLendersPositions.map(
                    (lp: { lender: StableKey; amount: string; id?: string }) => ({
                        lenders: lp.lender,
                        amount: Number(lp.amount),
                        id: lp.id,
                    })
                )
                : [{ ...defaultValue }]

        const subtotal =
            (effectiveLendersPositions?.length ?? 0) > 0
                ? effectiveLendersPositions.reduce(
                    (accumulator: number, lp: { amount: string }) =>
                        accumulator + Number(lp.amount),
                    0
                )
                : 0

        form.setFieldValue('lendersPosition', lendersPosition)
        form.setFieldValue('subtotal', subtotal)
    }, [effectiveLendersPositions])

    // Keep subtotal in sync when user edits amounts
    useEffect(() => {
        const subtotal = (form.values.lendersPosition ?? [])
            .map((lp) => (lp.amount ? Number(lp.amount) : 0))
            .reduce((acc, v) => acc + v, 0)
        if (subtotal !== form.values.subtotal) {
            form.setFieldValue('subtotal', subtotal)
        }
    }, [form.values.lendersPosition])

    const loanAmount = Number(loan?.amount)
    const selectedOptions = form.values.lendersPosition?.map(
        (item: { lenders: { id: string } }) => item.lenders.id
    )

    const fields = form.values.lendersPosition?.map(
        (
            item: { lenders: { id: string }; newField?: boolean | null },
            index: number
        ) => {
            const selectedOption = item.lenders.id
            const updatedOptions = lendersArray.map((option) => ({
                ...option,
                disabled:
                    selectedOptions.includes(option.value) && option.value !== selectedOption,
            }))

            return (
                <Group grow key={index}>
                    <Flex direction={'column'}>
                        <Grid
                            grow
                            style={{
                                height: '25%',
                                paddingBottom: '10px',
                            }}
                        >
                            <Grid.Col span={5}>
                                <Select
                                    searchable
                                    withAsterisk
                                    label="Lender"
                                    data={updatedOptions}
                                    placeholder="Select a Lender"
                                    disabled={disableLenderPositions && !item.newField}
                                    {...form.getInputProps(`lendersPosition.${index}.lenders.id`)}
                                />
                            </Grid.Col>
                            <Grid.Col span={5}>
                                <NumberInput
                                    withAsterisk
                                    label="Amount"
                                    precision={2}
                                    placeholder="Enter amount"
                                    disabled={disableLenderPositions}
                                    min={0}
                                    defaultValue={0}
                                    parser={(value) => value.replace(/[^0-9.]/g, '')}
                                    formatter={(value) =>
                                        !Number.isNaN(parseFloat(value))
                                            ? formatNumberToCurrency(
                                                Number(
                                                    parseFloat(
                                                        value.replace(
                                                            /\B(?<!\d*)(?=(\d{3})+(?!\d))/g,
                                                            ','
                                                        )
                                                    )
                                                ),
                                                loan?.currency ?? 'USD'
                                            )
                                            : '$ 0'
                                    }
                                    {...form.getInputProps(`lendersPosition.${index}.amount`)}
                                    sx={{
                                        label: {
                                            fontFamily: 'Plus Jakarta Sans',
                                            color: '#111928',
                                            fontSize: '1em',
                                        },
                                        input: {
                                            backgroundColor: '#F9FAFB',
                                            height: '3em',
                                            borderRadius: '0.7em',
                                            color: '#6B7280',
                                            borderWidth: '1px',
                                            borderColor: '#D1D5DB',
                                            fontSize: '1em',
                                            fontFamily: 'Plus Jakarta Sans',
                                        },
                                    }}
                                />
                            </Grid.Col>
                            {disableLenderPositions ? (
                                <Grid.Col
                                    span={2}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                ></Grid.Col>
                            ) : (
                                <Grid.Col
                                    span={2}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontFamily: 'Plus Jakarta Sans',
                                            color: '#111928',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            paddingTop: '5px',
                                        }}
                                    >
                                        Remove
                                    </span>
                                    <IonIcon
                                        name="trash-outline"
                                        className="click-ion-icon"
                                        style={{ color: 'red' }}
                                        onClick={() =>
                                            form.removeListItem('lendersPosition', index)
                                        }
                                    />
                                </Grid.Col>
                            )}
                        </Grid>
                    </Flex>
                </Group>
            )
        }
    )

    const handleClick = async () => {
        const transformedPositions = form.getTransformedValues().lendersPosition
        if (form.validate().hasErrors === true) {
            throw Error
        } else {
            let response: any

            // Determine if we should create/update an amendment:
            // 1. If amendment prop is true (explicit amendment action)
            // 2. If loan status is Approved/Locked (auto-create amendment/edit when editing approved loan)
            // 3. If loan already has amendmentType (continuing an edit/amendment in progress)
            const shouldCreateAmendment = amendment || (loan?.status === 'Approved' || loan?.status === 'Locked') || !!loan?.amendmentType
            if (shouldCreateAmendment) {
                const amendmentLenders = transformedPositions.map(
                    (lp: { lender: StableKey; amount: string; id?: string }) => {
                        return { lender: lp.lender, amount: lp.amount, id: lp.id }
                    }
                )
                // Try to find amendment data in store (only use if it exists as a draft amendment)
                const existingAmendment = loanAmenedmentFacility.data.find(
                    (la: any) => la.id === loan.id
                )

                // Use existing amendment data if available, otherwise use current loan for creating new amendment
                const loanAmendmentData = existingAmendment || loan

                if (!loanAmendmentData?.facilityId) {
                    ErrorNotification({
                        title: 'Loan Amendment Failed',
                        message: 'Facility ID is required for loan amendment',
                    })
                    return
                }

                try {
                    response = await dispatch(
                        saveLoanAmendment(
                            {
                                ...loanAmendmentData,
                                amendmentLenders: amendmentLenders,
                                // Set amendmentType: LoanEdit for edits, LoanAmendment for amendments
                                amendmentType: amendment ? 'LoanAmendment' : 'LoanEdit',
                                // Only set amendmentDate if this is an actual amendment (not an edit)
                                // Edit should have no amendmentDate (undefined), Amendment should have one
                                amendmentDate: amendment ? loanAmendmentData.amendmentDate : undefined,
                            },
                            existingAmendment // Only pass this if we found an existing draft amendment, otherwise undefined for creation
                        )
                    )

                    if (response?.success === false) {
                        ErrorNotification({
                            title: 'Loan Amendment Failed',
                            message:
                                response?.payload || 'Check Lender Allocations and try again',
                        })
                        return
                    }

                    if (response?.success === true) {
                        setLoan({
                            ...loan,
                            amendmentLenders: amendmentLenders,
                        })
                        SuccessNotification({
                            title: 'Successful Loan Amendment',
                            message: 'You can amend Lender Allocations',
                        })
                    } else {
                        ErrorNotification({
                            title: 'Loan Amendment Failed',
                            message: 'Unexpected response from server',
                        })
                    }
                    return
                } catch (error) {
                    console.log(error)
                    ErrorNotification({
                        title: 'Loan Amendment Failed',
                        message: 'An error occurred while saving the amendment',
                    })
                    return
                }
            }

            try {
                await dispatch(
                    changeAllLendersForLoan(transformedPositions, {
                        id: loan.id || '',
                        admin: loan.agencyAdmin,
                    })
                )
                SuccessNotification({
                    title: 'Successful Loan Creation',
                    message:
                        'You have successfully created a loan with the selected lender position',
                })
            } catch {
                ErrorNotification({
                    title: ' Loan Creation Failed',
                    message: 'Check Lender Allocations and try again',
                })
            }
        }
    }

    useImperativeHandle(ref, () => ({
        handleClick,
    }))

    return !isLoadingLenders ? (
        <div>
            {disableLenderPositions ? (
                <Grid.Col span={12} className="topFormErrorText">
                    Lender Positions cannot be changed for this loan.
                </Grid.Col>
            ) : null}
            {fields}
            <Group>
                {disableLenderPositions ? (
                    <Grid.Col span={5}></Grid.Col>
                ) : (
                    <Grid.Col span={5}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <IonIcon
                                name="add-circle-outline"
                                className="click-ion-icon"
                                onClick={() =>
                                    form.insertListItem('lendersPosition', {
                                        ...defaultValue,
                                        newField: true,
                                    })
                                }
                            />
                            <span
                                style={{
                                    marginLeft: '8px',
                                    fontFamily: 'Plus Jakarta Sans',
                                    fontWeight: 'bold',
                                    fontSize: '1em',
                                }}
                            >
                                Add lenders and amounts
                            </span>
                        </div>
                    </Grid.Col>
                )}
                <Grid.Col span={5}>
                    <NumberInput
                        label="Subtotal"
                        placeholder="Allocation Total"
                        min={0}
                        defaultValue={0}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                        disabled
                        precision={2}
                        formatter={(value) =>
                            !Number.isNaN(parseFloat(value))
                                ? formatNumberToCurrency(
                                    Number(value ?? 0),
                                    loan?.currency ?? 'USD'
                                )
                                : loan?.currency ?? 'USD'
                        }
                        {...form.getInputProps(`subtotal`)}
                        sx={{
                            label: {
                                fontFamily: 'Plus Jakarta Sans',
                                color: '#111928',
                                fontSize: '1em',
                                marginLeft: '-10px',
                                width: '102%',
                            },
                            input: {
                                backgroundColor: '#F9FAFB',
                                height: '3em',
                                borderRadius: '0.7em',
                                color: '#6B7280',
                                borderWidth: '1px',
                                borderColor: '#D1D5DB',
                                fontSize: '1em',
                                fontFamily: 'Plus Jakarta Sans',
                                marginLeft: '-10px',
                                width: '102%',
                            },
                        }}
                    />
                </Grid.Col>
            </Group>
            {form.values.subtotal > loanAmount + 0.011 ? (
                <Group>
                    <Grid.Col
                        span={10}
                        style={{ textAlign: 'right', marginLeft: '8px' }}
                    >
                        <span style={{ color: '#dc4d01' }}>
                            Total Lender Allocations should not exceed Loan Amount.
                        </span>
                    </Grid.Col>
                </Group>
            ) : null}
        </div>
    ) : (
        <div></div>
    )
}

export default React.forwardRef(ManageLoanLenders)
